import classNames from 'classnames';
import { useCallback, useState } from 'react';
import { JsonLd } from 'react-schemaorg';
import { FAQPage } from 'schema-dts';

import Typography from '../../core/Typography';
import Button from '../../core/Button';

import useStyles from './styles';
import IFaqExpandedSection from './types';

function FaqExpandedSection({ title, description, questions, className }: IFaqExpandedSection) {
  const classes = useStyles();

  const [questionsVisible, setQuestionsVisible] = useState<number>(3);

  const loadAll = useCallback(() => {
    if (questionsVisible >= questions.length) {
      return;
    }
    setQuestionsVisible(questions.length);
  }, [questions.length, questionsVisible]);

  return (
    <section className={classNames(classes.container, className)}>
      <div className={classes.root}>
        <div className={classes.text}>
          <Typography className={classes.label} element="p">
            FAQs
          </Typography>
          <Typography className={classes.title} element="h1">
            {title}
          </Typography>
          <Typography className={classes.description} element="p">
            {description}
          </Typography>
        </div>
        <div className={classes.questions}>
          {questions.map((question, index) => (
            <div
              className={classNames(
                classes.question,
                index >= questionsVisible && classes.hiddenQuestion,
              )}
              key={index}
            >
              <Typography className={classes.questionTitle} element="h2">
                {question.question}
              </Typography>
              <Typography className={classes.questionAnswer} element="p">
                {question.answer}
              </Typography>
            </div>
          ))}
          {questionsVisible < questions.length && <div className={classes.questionsOverlay} />}
          {questionsVisible < questions.length && (
            <Button onClick={loadAll}>Load all questions</Button>
          )}
        </div>
      </div>

      <JsonLd<FAQPage>
        item={{
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: questions.map((question) => ({
            '@type': 'Question',
            name: question.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: typeof question.answer !== 'string' ? question.answerSchema : question.answer,
            },
          })),
        }}
      />
    </section>
  );
}

export default FaqExpandedSection;
